import { useProjectStore } from './store/useProjectStore';
import { useExtractionStore } from './store/useExtractionStore';
import { useReviewStore } from './store/useReviewStore';
import { useCollectionStore } from './store/useCollectionStore';
import { useAssignmentStore } from './store/useAssignmentStore';
import * as api from './api/endpoints';

const SESSION_KEY = 'monco_session_id';

export async function initApp(): Promise<{ multiProject: boolean }> {
  const store = useProjectStore.getState();

  // 1. Check session status first to detect multi-project mode
  const session = await api.getSessionStatus();
  const multiProject = session?.multi_project ?? false;

  // 2. Load project metadata (may be null if no project is active yet)
  const project = await api.getProject().catch(() => null);
  if (!project) return { multiProject }; // No project yet — HomeScreen will handle it
  store.setProject(project);

  // 3. Session setup
  if (session) {
    sessionStorage.setItem(SESSION_KEY, session.session_id);
    store.setSessionId(session.session_id);

    // HITL mode: disable auto-approve so page reviews pause for user
    if (session.hitl) {
      useExtractionStore.getState().setAutoApprovePages(false);
    }

    // Check if results already exist
    const skills = await api.getSkills();
    if (skills) store.setSkills(skills);
  }

  // 4. Signal startup ready
  await api.startupReady();

  // 5. Always load existing results
  await loadAllResults();

  return { multiProject };
}

export async function loadAllResults() {
  const store = useProjectStore.getState();
  const extStore = useExtractionStore.getState();

  // Load renders
  const renders = await api.getRenders();
  if (renders) store.setRenders(renders);

  // Load skill results + published plans + page results in parallel
  const [triage, separation, research, extraction, boq, preScan, publishedPlans, pageResults] =
    await Promise.all([
      api.getTriage(),
      api.getSeparation(),
      api.getResearchReport(),
      api.getExtraction(),
      api.getBoqExtraction(),
      api.getPreScan(),
      api.getPublishedPlans().catch(() => null),
      api.getExtractionPageResults().catch(() => null),
    ]);

  if (triage) store.setTriage(triage);
  if (separation) store.setSeparation(separation);
  if (research) store.setResearchReport(research);
  if (extraction) store.setExtraction(extraction);
  if (boq) store.setBoqExtraction(boq);
  if (preScan) store.setPreScan(preScan);

  // Restore published plans
  if (publishedPlans) {
    const mapped: Record<string, { published_at: string; location: string }> = {};
    for (const [key, val] of Object.entries(publishedPlans)) {
      mapped[key] = { published_at: val.published_at, location: val.location };
    }
    extStore.setPublishedPlans(mapped);
  }

  // Load review data
  const reviewData = await api.getReviewData().catch(() => null);
  if (reviewData) useReviewStore.getState().setReviewData(reviewData);

  // Load assignments for the current project
  const projectId = store.project?.project_id;
  if (projectId) {
    useAssignmentStore.getState().load(projectId).catch(() => {});
  }

  // Load collection state
  const collected = await api.getCollectedState().catch(() => null);
  if (collected) {
    useCollectionStore.getState().restore(collected.diagrams || {}, collected.plans || {});
  }

  // Restore extraction page statuses. Merge BOTH sources: the saved
  // diagram_extraction.json zones form the complete base, and the in-memory
  // page results (fresher, survive reload during a live run) overlay them.
  // Using only the in-memory set would hide already-saved pages whenever it's
  // partial (e.g. after a server restart — only pages extracted since then are
  // in memory), leaving those pages stuck "pending" and un-collectable.
  {
    const results: Record<string, import('./types').ExtractionPageResult> = {};
    if (extraction?.zones) {
      for (const zone of extraction.zones) {
        for (const page of zone.pages || []) {
          results[`${page.file}:${page.page}`] = page;
        }
      }
    }
    if (pageResults) {
      for (const [key, val] of Object.entries(pageResults)) {
        results[key] = val as import('./types').ExtractionPageResult;
      }
    }
    if (Object.keys(results).length > 0) {
      extStore.restorePageResults(results);
    }
  }
}
